/**
 * Tweakpane tuning panel (F1): one folder per tuning group plus the render
 * feel values. Changes apply live and persist in localStorage; Export copies
 * the JSON to the clipboard and logs it.
 */

import { PAD_SCHEMES } from "@lache/input";
import {
  FEEL_GROUPS,
  mergeFeel,
  mergePoses,
  POSE_KEYS,
  POSE_NAMES,
  type Pose,
  type PoseName,
  type RenderFeel,
} from "@lache/render";
import { mergeTuning, TUNING_GROUPS, TUNING_KEYS, type Tuning, tuningHash } from "@lache/sim";
import { Pane } from "tweakpane";

/**
 * Saved tuning and feel hold only the keys the owner moved away from the defaults, so a default
 * that changes in code takes effect unless the owner set that key on purpose. The v0 tuning save
 * was the whole object; loading it once keeps its deliberate changes and drops the keys whose
 * defaults moved on 2026-09-14 (the pad scheme and the pump stick), which a full save would pin.
 */
const STORAGE_TUNING = "lache.tuning.v1";
const LEGACY_TUNING = "lache.tuning.v0";
const LEGACY_PINNED: (keyof Tuning)[] = ["padScheme", "pumpStick"];
const STORAGE_FEEL = "lache.feel.v0";
const STORAGE_POSES = "lache.poses.v0";

/** The keys of `obj` whose numbers differ from `defaults`. */
function diffNumbers<T extends object>(obj: T, defaults: T, keys: (keyof T)[]): Partial<T> {
  const out: Partial<T> = {};
  for (const k of keys) if (obj[k] !== defaults[k]) out[k] = obj[k];
  return out;
}

export interface PanelHost {
  tuning: Tuning;
  feel: RenderFeel;
  poses: Record<PoseName, Pose>;
  defaultTuning: Tuning;
  defaultFeel: RenderFeel;
  defaultPoses: Record<PoseName, Pose>;
  onTuningChange: () => void;
  onFeelChange: () => void;
  onPosesChange: () => void;
  onPreview: (pose: PoseName | null) => void;
  onAction: (name: string) => void;
}

/** Ranges for keys whose useful span is not three times their default: the input scheme and thresholds. */
const TUNING_RANGES: Partial<Record<keyof Tuning, { min: number; max: number; step: number }>> = {
  padScheme: { min: 0, max: PAD_SCHEMES.length - 1, step: 1 },
  triggerActionThreshold: { min: 0, max: 1, step: 0.01 },
  stickRunAssist: { min: 0, max: 1, step: 0.01 },
  stickRunAssistMinX: { min: 0, max: 1, step: 0.01 },
  barStickJumpDeg: { min: 0, max: 90, step: 1 },
  gripRelease: { min: 0, max: 1, step: 0.01 },
};

function range(def: number): { min: number; max: number; step: number } {
  if (def === 0) return { min: -1, max: 1, step: 0.01 };
  const mag = Math.abs(def);
  const max = mag * 3;
  const step =
    Number.isInteger(def) && mag >= 5 ? 1 : mag >= 100 ? 1 : mag >= 10 ? 0.1 : mag >= 1 ? 0.01 : 0.001;
  return { min: def < 0 ? -max : 0, max, step };
}

export function loadSaved<T extends object>(key: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return null;
  }
}

/**
 * Set once when a saved pad scheme was dropped so the current default scheme applies; a scheme chosen
 * after that is saved and kept as usual. The key's version moves with the default: v5 was the owner's
 * pick of 2026-09-14, v6 the pick of 2026-09-18 (B the only crouch, the left trigger the rotate hold).
 */
const SCHEME_DEFAULT_RESET = "lache.schemeDefault.v6";

export function loadSavedTuning(defaults: Tuning): Tuning {
  const saved = loadSaved<Tuning>(STORAGE_TUNING);
  try {
    if (!localStorage.getItem(SCHEME_DEFAULT_RESET)) {
      localStorage.setItem(SCHEME_DEFAULT_RESET, "1");
      if (saved && "padScheme" in saved) {
        delete saved.padScheme;
        localStorage.setItem(STORAGE_TUNING, JSON.stringify(saved));
      }
    }
  } catch {
    // Storage unavailable: nothing saved to reset.
  }
  if (saved) return mergeTuning(defaults, saved);
  const legacy = loadSaved<Tuning>(LEGACY_TUNING);
  if (!legacy) return mergeTuning(defaults, null);
  const kept = diffNumbers(mergeTuning(defaults, legacy), defaults, TUNING_KEYS);
  for (const k of LEGACY_PINNED) delete kept[k];
  try {
    localStorage.setItem(STORAGE_TUNING, JSON.stringify(kept));
    localStorage.removeItem(LEGACY_TUNING);
  } catch {
    // Storage unavailable: the migrated values still apply for this load.
  }
  return mergeTuning(defaults, kept);
}

export function loadSavedFeel(defaults: RenderFeel): RenderFeel {
  return mergeFeel(defaults, loadSaved<RenderFeel>(STORAGE_FEEL));
}

export function loadSavedPoses(defaults: Record<PoseName, Pose>): Record<PoseName, Pose> {
  return mergePoses(defaults, loadSaved<Record<PoseName, Pose>>(STORAGE_POSES));
}

export class TuningPanel {
  readonly pane: Pane;
  private readonly hashLabel: { hash: string };
  private visible = false;

  constructor(private readonly host: PanelHost) {
    this.pane = new Pane({ title: "Lache tuning (F1)", expanded: true });
    this.hashLabel = { hash: tuningHash(host.tuning) };
    this.pane.addBinding(this.hashLabel, "hash", { readonly: true, label: "tuning hash" });

    const actions = this.pane.addFolder({ title: "actions", expanded: true });
    for (const name of ["export", "reset tuning", "reset feel", "restart at start", "clear saved"]) {
      actions.addButton({ title: name }).on("click", () => this.action(name));
    }

    for (const [group, keys] of Object.entries(TUNING_GROUPS)) {
      const folder = this.pane.addFolder({ title: `sim: ${group}`, expanded: false });
      for (const key of keys) {
        const r = TUNING_RANGES[key] ?? range(host.defaultTuning[key]);
        folder.addBinding(host.tuning, key, r).on("change", () => this.tuningChanged());
      }
    }
    for (const [group, keys] of Object.entries(FEEL_GROUPS)) {
      const folder = this.pane.addFolder({ title: `render: ${group}`, expanded: false });
      for (const key of keys) {
        const r = range(host.defaultFeel[key]);
        folder.addBinding(host.feel, key, r).on("change", () => this.feelChanged());
      }
    }
    this.buildPoseEditor();
    this.setVisible(false);
  }

  private readonly editor = { pose: "idle" as PoseName, preview: false };
  private poseBindings: { dispose(): void }[] = [];
  private poseFolder: ReturnType<Pane["addFolder"]> | null = null;

  private buildPoseEditor(): void {
    const folder = this.pane.addFolder({ title: "pose editor", expanded: false });
    this.poseFolder = folder;
    const options: Record<string, PoseName> = {};
    for (const n of POSE_NAMES) options[n] = n;
    folder.addBinding(this.editor, "pose", { options }).on("change", () => {
      this.rebuildPoseSliders();
      if (this.editor.preview) this.host.onPreview(this.editor.pose);
    });
    folder.addBinding(this.editor, "preview").on("change", () => {
      this.host.onPreview(this.editor.preview ? this.editor.pose : null);
    });
    folder.addButton({ title: "export poses" }).on("click", () => {
      const json = JSON.stringify(this.host.poses, null, 2);
      console.log(json);
      void navigator.clipboard?.writeText(json);
    });
    folder.addButton({ title: "reset this pose" }).on("click", () => {
      Object.assign(this.host.poses[this.editor.pose], this.host.defaultPoses[this.editor.pose]);
      this.posesChanged();
      this.pane.refresh();
    });
    this.rebuildPoseSliders();
  }

  private rebuildPoseSliders(): void {
    for (const b of this.poseBindings) b.dispose();
    this.poseBindings = [];
    const folder = this.poseFolder;
    if (!folder) return;
    const pose = this.host.poses[this.editor.pose];
    for (const key of POSE_KEYS) {
      const r = key === "hipY" ? { min: 0.2, max: 1.2, step: 0.01 } : { min: -180, max: 180, step: 1 };
      const b = folder.addBinding(pose, key, r).on("change", () => this.posesChanged());
      this.poseBindings.push(b);
    }
  }

  private posesChanged(): void {
    localStorage.setItem(STORAGE_POSES, JSON.stringify(this.host.poses));
    this.host.onPosesChange();
  }

  private tuningChanged(): void {
    this.hashLabel.hash = tuningHash(this.host.tuning);
    localStorage.setItem(
      STORAGE_TUNING,
      JSON.stringify(diffNumbers(this.host.tuning, this.host.defaultTuning, TUNING_KEYS)),
    );
    this.host.onTuningChange();
  }

  private feelChanged(): void {
    const keys = Object.keys(this.host.defaultFeel) as (keyof RenderFeel)[];
    localStorage.setItem(
      STORAGE_FEEL,
      JSON.stringify(diffNumbers(this.host.feel, this.host.defaultFeel, keys)),
    );
    this.host.onFeelChange();
  }

  /** Set one tuning value from outside the pane (the controls menu), with the same save and notify. */
  setTuning<K extends keyof Tuning>(key: K, value: Tuning[K]): void {
    this.host.tuning[key] = value;
    this.tuningChanged();
    this.pane.refresh();
  }

  private action(name: string): void {
    switch (name) {
      case "export": {
        const json = JSON.stringify({ tuning: this.host.tuning, feel: this.host.feel }, null, 2);
        console.log(json);
        void navigator.clipboard?.writeText(json);
        break;
      }
      case "reset tuning":
        Object.assign(this.host.tuning, this.host.defaultTuning);
        this.tuningChanged();
        this.pane.refresh();
        break;
      case "reset feel":
        Object.assign(this.host.feel, this.host.defaultFeel);
        this.feelChanged();
        this.pane.refresh();
        break;
      case "clear saved":
        localStorage.removeItem(STORAGE_TUNING);
        localStorage.removeItem(STORAGE_FEEL);
        localStorage.removeItem(STORAGE_POSES);
        break;
      default:
        break;
    }
    this.host.onAction(name);
  }

  toggle(): void {
    this.setVisible(!this.visible);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.pane.element.style.display = v ? "" : "none";
  }

  refresh(): void {
    this.hashLabel.hash = tuningHash(this.host.tuning);
    this.pane.refresh();
  }
}
